import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { getPublishedPortalSnapshot, unlockPublishedPortal } from '../api/publicClient.js';
import type {
  PublishedPortalScorecardRecord,
  PublishedPortalSnapshotRecord,
  PublishedPortalWeekRecord,
} from '../api/types.js';
import { EmptyState, ErrorPanel, LoadingPanel, PageIntro, PanelHeader } from './components.js';
import { mapError } from './errors.js';
import { formatDate, formatOneDecimal } from '../lib/formatting.js';
import { buildPortalWeekSkinsTable } from './portalSnapshotViews.js';

function badge(status: string) {
  return <span className={`badge ${status || 'unknown'}`}>{status || 'unknown'}</span>;
}

function handicapMeta(handicapIndex: number | null, courseHandicap: number | null): string {
  const parts: string[] = [];
  if (typeof handicapIndex === 'number' && Number.isFinite(handicapIndex)) {
    parts.push(`HI ${formatOneDecimal(handicapIndex)}`);
  }
  if (typeof courseHandicap === 'number' && Number.isFinite(courseHandicap)) {
    parts.push(`CH ${Math.round(courseHandicap)}`);
  }
  return parts.join(' | ');
}

function normalizeDisplayName(name: string | null | undefined, fallback: string): string {
  const normalized = (name ?? '')
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized.length > 0 ? normalized : fallback;
}

function normalizeDisplayNameForRecap(name: string | null | undefined, fallback: string): string {
  return normalizeDisplayName(name, fallback).replace(/ /g, '\u00A0');
}

function scorecardRoleLabel(scorecard: PublishedPortalScorecardRecord): string | null {
  if (scorecard.card_type === 'standalone') {
    return scorecard.participant_type === 'sub' ? 'Playoff week sub round' : 'Playoff week round';
  }
  if (scorecard.card_type !== 'supplemental') {
    return null;
  }
  return 'Sub scorecard';
}

function scorecardTitle(scorecard: PublishedPortalScorecardRecord): string {
  if (scorecard.card_type === 'supplemental') {
    return normalizeDisplayNameForRecap(scorecard.players[0]?.playerName, 'Supplemental scorecard');
  }
  if (scorecard.card_type === 'standalone') {
    return normalizeDisplayNameForRecap(scorecard.players[0]?.playerName, 'Playoff week round');
  }
  const playerAName = normalizeDisplayNameForRecap(scorecard.players[0]?.playerName, 'Player A');
  const playerBName = normalizeDisplayNameForRecap(scorecard.players[1]?.playerName, 'Player B');
  return `${playerAName} vs ${playerBName}`;
}

function scorecardSubtitle(scorecard: PublishedPortalScorecardRecord): string {
  const base = `${formatDate(scorecard.date)} | ${scorecard.courseName}`;
  if (scorecard.card_type === 'supplemental') {
    return `${base} | For absent ${scorecard.for_player_name ?? 'scheduled player'}`;
  }
  if (scorecard.card_type === 'standalone') {
    return scorecard.participant_type === 'sub'
      ? `${base} | Sub for absent ${scorecard.for_player_name ?? 'scheduled player'}`
      : `${base} | Standalone playoff-week round`;
  }
  return `${base} | ${scorecard.status === 'completed' ? 'Scored card' : 'Blank card'}`;
}

function scorecardHeaderBadge(scorecard: PublishedPortalScorecardRecord): string | null {
  const roleLabel = scorecardRoleLabel(scorecard);
  if (roleLabel) {
    return roleLabel;
  }
  return scorecard.players.some((player) => player.playerAbsent) ? 'Absent player' : null;
}

function useGuestLeagueId(): string {
  const params = useParams();
  const leagueId = params.leagueId;
  if (!leagueId) {
    throw new Error('Guest portal route is missing leagueId');
  }
  return leagueId;
}

export function GuestPortalPage() {
  const leagueId = useGuestLeagueId();
  const [password, setPassword] = useState('');
  const snapshotQuery = useQuery({
    queryKey: ['guest-portal', leagueId],
    queryFn: async () => (await getPublishedPortalSnapshot(leagueId)).snapshot,
    retry: false,
  });
  const unlockMutation = useMutation({
    mutationFn: async () => unlockPublishedPortal(leagueId, { password: password.trim() }),
    onSuccess: async () => {
      setPassword('');
      await snapshotQuery.refetch();
    },
  });

  if (snapshotQuery.isLoading) {
    return <LoadingPanel title="Guest Portal" message="Loading published portal." />;
  }

  if (snapshotQuery.isError) {
    const status = 'status' in (snapshotQuery.error ?? {}) ? Number((snapshotQuery.error as { status?: number }).status) : 500;
    if (status === 401) {
      return (
        <>
          <section className="panel stack">
            <PanelHeader title="Unlock Portal" />
            <form
              className="stack"
              onSubmit={(event) => {
                event.preventDefault();
                void unlockMutation.mutateAsync();
              }}
            >
              <div className="field">
                <label htmlFor="guest-portal-password">League Password</label>
                <input
                  id="guest-portal-password"
                  type="password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              {unlockMutation.isError ? <div className="alert error">{mapError(unlockMutation.error)}</div> : null}
              <div className="inline-actions">
                <button type="submit" disabled={unlockMutation.isPending}>
                  {unlockMutation.isPending ? 'Unlocking...' : 'Unlock Portal'}
                </button>
              </div>
            </form>
          </section>
        </>
      );
    }

    if (status === 404) {
      return (
        <>
          <PageIntro
            title="Published Member Portal"
          />
          <section className="panel">
            <EmptyState
              title="Portal unavailable"
              message="Ask your commissioner to publish the season snapshot and share the portal password."
            />
          </section>
        </>
      );
    }

    return <ErrorPanel title="Guest Portal" message={mapError(snapshotQuery.error)} />;
  }

  if (!snapshotQuery.data) {
    return <ErrorPanel title="Guest Portal" message="Published portal data is unavailable." />;
  }

  return <GuestPortalContent snapshot={snapshotQuery.data} />;
}

function GuestPortalContent({ snapshot }: { snapshot: PublishedPortalSnapshotRecord }) {
  return (
    <>
      <PageIntro
        title={snapshot.league_name}
      />
      <section className="panel">
        <PanelHeader title="Standings" />
        {snapshot.standings.length === 0 ? (
          <EmptyState title="No standings yet" message="" />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Player</th>
                  <th>Total Points</th>
                  <th>Matches</th>
                </tr>
              </thead>
              <tbody>
                {snapshot.standings.map((row, index) => (
                  <tr key={row.playerId}>
                    <td>{index + 1}</td>
                    <td>{row.playerName}</td>
                    <td>{row.totalPoints}</td>
                    <td>{row.matchesPlayed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="panel stack">
        <PanelHeader title="Schedule, Results, and Scorecards" />
        {snapshot.weeks.length === 0 ? (
          <EmptyState title="No match days published" message="" />
        ) : (
          snapshot.weeks.map((week) => (
            <article key={week.matchDayId} className="stack match-day-panel">
              <div className="week-head"><h3>Week {week.weekNumber} <span className="muted">· {formatDate(week.date)} · {week.courseName}</span></h3></div>
              {week.matches.length === 0 ? (
                <EmptyState title="No matches assigned" message="" />
              ) : (
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Player A</th>
                        <th>Player B</th>
                        <th>Status</th>
                        <th>Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {week.matches.map((match) => (
                        <tr key={match.id}>
                          <td>
                            {match.playerAName}
                            {match.status === 'scheduled' ? (
                              <div className="muted">{handicapMeta(match.playerAHandicapIndex, match.playerACourseHandicap)}</div>
                            ) : null}
                          </td>
                          <td>
                            {match.playerBName}
                            {match.status === 'scheduled' ? (
                              <div className="muted">{handicapMeta(match.playerBHandicapIndex, match.playerBCourseHandicap)}</div>
                            ) : null}
                          </td>
                          <td>{match.status === 'completed' ? null : badge(match.status)}</td>
                          <td>
                            {match.playerAPoints} - {match.playerBPoints}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {week.scorecards.length > 0 ? (
                <div className="stack guest-week-scorecards">
                  {week.scorecards.map((scorecard) => (
                    <GuestPortalScorecard key={scorecard.id} scorecard={scorecard} condensed />
                  ))}
                </div>
              ) : week.courseId ? null : (
                <div className="muted">Scorecards unavailable until a course is assigned.</div>
              )}
              <PortalWeekSkinsTable week={week} />
            </article>
          ))
        )}
      </section>
    </>
  );
}

function scorecardValueClass(
  value: number | null | undefined,
  hasData: boolean,
): string | undefined {
  if (!hasData) {
    return undefined;
  }
  return value === null || value === undefined ? 'muted' : undefined;
}

function grossScoreDecoration(
  score: number | null | undefined,
  par: number | undefined,
  presentation: 'default' | 'recap-export',
): string | null {
  if (presentation !== 'recap-export' || score === null || score === undefined || par === undefined) {
    return null;
  }

  const delta = score - par;
  if (delta <= -2) {
    return 'gross-score-symbol gross-score-symbol-double-circle';
  }
  if (delta === -1) {
    return 'gross-score-symbol gross-score-symbol-circle';
  }
  if (delta === 1) {
    return 'gross-score-symbol gross-score-symbol-square';
  }
  if (delta >= 2) {
    return 'gross-score-symbol gross-score-symbol-double-square';
  }
  return null;
}

export function PortalWeekSkinsTable({
  week,
  className = '',
}: {
  week: PublishedPortalWeekRecord;
  className?: string;
}) {
  const rows = buildPortalWeekSkinsTable(week);
  if (rows.length === 0) {
    return null;
  }

  const holes = Array.from({ length: rows[0]?.overallNetHoleScores.length ?? 0 }, (_, index) => index + 1);

  return (
    <section className={`stack guest-week-skins-panel${className ? ` ${className}` : ''}`}>
      <PanelHeader title="Overall Net Scores (For Skins)" />
      <div className="table-wrap">
        <table className="data-table guest-week-skins-table">
          <thead>
            <tr>
              <th>Player</th>
              {holes.map((hole) => (
                <th key={hole}>{hole}</th>
              ))}
              <th>TOT</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>
                  <strong>{row.playerName}</strong>
                  {row.subtitle ? <div className="muted guest-week-skins-subtitle">{row.subtitle}</div> : null}
                </td>
                {row.overallNetHoleScores.map((score, index) => (
                  <td key={`${row.key}-hole-${index}`} className={row.winningHoles[index] ? 'skins-best-hole' : undefined}>
                    {score}
                  </td>
                ))}
                <td>{row.overallNetScore}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function GuestPortalScorecard({
  scorecard,
  condensed = false,
  hideHandicapRow = false,
  presentation = 'default',
}: {
  scorecard: PublishedPortalScorecardRecord;
  condensed?: boolean;
  hideHandicapRow?: boolean;
  presentation?: 'default' | 'recap-export';
}) {
  const isSupplemental = scorecard.card_type === 'supplemental';
  const isStandalone = scorecard.card_type === 'standalone';
  const isSingleParticipantCard = isSupplemental || isStandalone;
  const holes = Array.from({ length: scorecard.holePars.length }, (_, index) => index + 1);
  const rowOrder =
    presentation === 'recap-export'
      ? (isSingleParticipantCard ? (['par', 'gross', 'strokes', 'net'] as const) : (['par', 'gross', 'strokes', 'net', 'points'] as const))
      : (isSingleParticipantCard
          ? (['par', 'handicap', 'strokes', 'gross', 'net'] as const)
          : (['par', 'handicap', 'strokes', 'gross', 'net', 'points'] as const));
  const headerBadge = scorecardHeaderBadge(scorecard);
  const roleLabel = scorecardRoleLabel(scorecard);
  return (
    <article className={`panel stack${condensed ? ' guest-scorecard-panel condensed' : ''}${isSingleParticipantCard ? ' supplemental' : ''}`}>
      <PanelHeader
        title={scorecardTitle(scorecard)}
        subtitle={scorecardSubtitle(scorecard)}
        actions={
          headerBadge ? (
            <span className={`scorecard-context-badge${isSingleParticipantCard ? ' scorecard-context-badge-supplemental' : ''}`}>
              {headerBadge}
            </span>
          ) : null
        }
      />
      {scorecard.players.map((player, index) => (
        <section key={`${scorecard.id}-${player.playerId}-${index}`} className="match-detail-scorecard">
          <header className="match-detail-player-header">
            <div>
              {roleLabel ? <div className="match-detail-player-kicker">{roleLabel}</div> : null}
              <h3 className="match-detail-player-name">{normalizeDisplayNameForRecap(player.playerName, 'Player')}</h3>
              {handicapMeta(player.handicapIndex, player.courseHandicap) ? (
                <p className="match-detail-player-meta">{handicapMeta(player.handicapIndex, player.courseHandicap)}</p>
              ) : null}
            </div>
            {player.playerAbsent ? (
              <div
                className={`match-detail-player-absence-badge${
                  presentation === 'recap-export' ? ' match-detail-player-absence recap-export-player-absence' : ''
                }`}
              >
                Absent player
              </div>
            ) : null}
          </header>
          <div className="match-detail-table-wrap">
            <table className="match-detail-table">
              <thead>
                <tr>
                  <th></th>
                  {holes.map((hole) => (
                    <th key={hole} className={scorecard.hammerHole === hole ? 'hammer-col' : ''}>
                      {scorecard.hammerHole === hole ? <span className="hammer-marker">Hammer</span> : null}
                      {hole}
                    </th>
                  ))}
                  <th className="total-col">TOT</th>
                </tr>
              </thead>
              <tbody>
                {rowOrder.map((row) => {
                  if (row === 'par') {
                    return (
                      <tr key={`${player.playerId}-par`} className="row-reference">
                        <td>Par</td>
                        {scorecard.holePars.map((par, index) => (
                          <td
                            key={`${player.playerId}-par-${index}`}
                            className={scorecard.hammerHole === index + 1 ? 'hammer-col' : ''}
                          >
                            <span className="match-detail-reference-value">{par}</span>
                          </td>
                        ))}
                        <td className="total-col">{scorecard.holePars.reduce((sum, value) => sum + value, 0)}</td>
                      </tr>
                    );
                  }

                  if (row === 'handicap') {
                    if (hideHandicapRow) {
                      return null;
                    }
                    return (
                      <tr key={`${player.playerId}-handicap`} className="row-reference">
                        <td>Hdcp</td>
                        {scorecard.holeHandicaps.map((handicap, index) => (
                          <td
                            key={`${player.playerId}-hdcp-${index}`}
                            className={scorecard.hammerHole === index + 1 ? 'hammer-col' : ''}
                          >
                            <span className="match-detail-reference-value">{handicap}</span>
                          </td>
                        ))}
                        <td className="total-col">-</td>
                      </tr>
                    );
                  }

                  if (row === 'gross') {
                    return (
                      <tr key={`${player.playerId}-gross`}>
                        <td>Gross</td>
                        {holes.map((hole, index) => (
                          <td key={`${player.playerId}-gross-${hole}`} className={scorecard.hammerHole === hole ? 'hammer-col' : ''}>
                            {player.grossScores?.[index] === null || player.grossScores?.[index] === undefined ? (
                              <span className={scorecardValueClass(player.grossScores?.[index] ?? null, Boolean(player.grossScores))}>
                                {player.grossScores?.[index] ?? '--'}
                              </span>
                            ) : (
                              <span
                                className={
                                  grossScoreDecoration(player.grossScores[index], scorecard.holePars[index], presentation) ?? undefined
                                }
                              >
                                {player.grossScores[index]}
                              </span>
                            )}
                          </td>
                        ))}
                        <td className="total-col">
                          <span className={scorecardValueClass(player.grossTotal, player.grossScores !== null)}>
                            {player.grossTotal ?? '--'}
                          </span>
                        </td>
                      </tr>
                    );
                  }

                  if (row === 'strokes') {
                    return (
                      <tr key={`${player.playerId}-strokes`}>
                        <td>Strokes</td>
                        {player.strokes.map((stroke, index) => (
                          <td
                            key={`${player.playerId}-stroke-${index}`}
                            className={scorecard.hammerHole === index + 1 ? 'hammer-col' : ''}
                          >
                            {stroke}
                          </td>
                        ))}
                        <td className="total-col">{player.strokes.reduce((sum, value) => sum + value, 0)}</td>
                      </tr>
                    );
                  }

                  if (row === 'net') {
                    return (
                      <tr key={`${player.playerId}-net`}>
                        <td>Net</td>
                        {holes.map((hole, index) => (
                          <td key={`${player.playerId}-net-${hole}`} className={scorecard.hammerHole === hole ? 'hammer-col' : ''}>
                            <span className={scorecardValueClass(player.netScores?.[index] ?? null, Boolean(player.netScores))}>
                              {player.netScores?.[index] ?? '--'}
                            </span>
                          </td>
                        ))}
                        <td className="total-col">
                          <span className={scorecardValueClass(player.netTotal, player.netScores !== null)}>
                            {player.netTotal ?? '--'}
                          </span>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={`${player.playerId}-points`}>
                      <td>Points</td>
                      {holes.map((hole, index) => (
                        <td key={`${player.playerId}-points-${hole}`} className={scorecard.hammerHole === hole ? 'hammer-col' : ''}>
                          <span className={scorecardValueClass(player.holePoints?.[index] ?? null, Boolean(player.holePoints))}>
                            {player.holePoints?.[index] ?? '--'}
                          </span>
                        </td>
                      ))}
                      <td className="total-col">
                        <span className={scorecardValueClass(player.totalPoints, player.holePoints !== null)}>
                          {player.totalPoints ?? '--'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </article>
  );
}
